// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import { isNil, omitBy } from 'lodash'
import get from 'lodash/get'
import { inject } from 'mobx-react'
import React from 'react'
import { Mutation } from 'react-apollo'
import type { AuthStore } from '../../auth/types'
import { i18n } from '../../index'
import type { Station } from '../../station/stationTypes'
import withActiveStation from '../../station/withActiveStation'
import css from './vitrineProductForm.css'
import { availableVitrineProductsQuery, createVitrineProduct, updateVitrineProduct } from './vitrineProductQueries'
import type { VitrineProduct, VitrineProductType } from './vitrineProductTypes'
const defaultFormData = {
  id: null,
  name: '',
  stationId: null,
  wastePercent: null,
  type: 'Salty',
}

const updateCacheOnUpdate = (cache: any, { data: { updateVitrineProduct } }: any, stationId: string) => {
  const { availableVitrineProducts } = cache.readQuery({
    query: availableVitrineProductsQuery,
    variables: {
      stationId,
    },
  })
  cache.writeQuery({
    query: availableVitrineProductsQuery,
    variables: {
      stationId,
    },
    data: {
      availableVitrineProducts: [
        ...availableVitrineProducts.map((vitrineProduct) => {
          return vitrineProduct.id === updateVitrineProduct.id ? updateVitrineProduct : vitrineProduct
        }),
      ],
    },
  })
}

const updateCacheOnCreate = (cache: any, { data: { createVitrineProduct } }: any, stationId: string) => {
  const { availableVitrineProducts } = cache.readQuery({
    query: availableVitrineProductsQuery,
    variables: {
      stationId,
    },
  })
  cache.writeQuery({
    query: availableVitrineProductsQuery,
    variables: {
      stationId,
    },
    data: {
      availableVitrineProducts: [...availableVitrineProducts, createVitrineProduct],
    },
  })
}

type VitrineProductFormProps = {
  data: VitrineProduct | null | undefined
  onSubmit: (...args: Array<any>) => any
  onCancel: (...args: Array<any>) => any
  activeStation: Station
  authStore: AuthStore
}
type FormData = {
  id: string | null | undefined
  name: string
  stationId: string | null | undefined
  minimumQuantity: number | null | undefined
  type: VitrineProductType
}
type VitrineProductFormState = {
  formData: FormData
}

@inject('authStore')
@withActiveStation
class VitrineProductForm extends React.Component<VitrineProductFormProps, VitrineProductFormState> {
  state = {
    formData: { ...defaultFormData },
  }

  componentDidMount() {
    const { data, activeStation } = this.props

    if (data) {
      this.setStateFromData(data)
    } else {
      this.onInput('stationId', activeStation.id)
    }
  }

  componentDidUpdate(prevProps: VitrineProductFormProps) {
    const { data } = this.props

    if (data && JSON.stringify(data) !== JSON.stringify(prevProps.data)) {
      this.setStateFromData(data)
    }
  }

  setStateFromData = (data: VitrineProduct) => {
    this.setState({
      formData: {
        ...defaultFormData,
        id: data.id,
        name: data.name,
        stationId: get(data, 'station.id', null),
        type: data.type,
        wastePercent: data.wastePercent,
      },
    })
  }
  onInput = (fieldName: string, newValue: (string | null | undefined) | number) => {
    this.setState({
      formData: { ...this.state.formData, [fieldName]: newValue },
    })
  }
  onSubmit = (onMutate: (...args: Array<any>) => any, e: any) => {
    e.preventDefault()
    const { activeStation } = this.props
    const { formData } = this.state
    const vitrineProduct = omitBy(
      {
        name: formData.name,
        type: formData.type,
        wastePercent: formData.wastePercent && Number(formData.wastePercent),
      },
      isNil,
    )
    onMutate(
      formData.id
        ? {
            variables: {
              vitrineProductId: formData.id,
              vitrineProduct,
            },
            update: (cache: any, mutationResult: any) => updateCacheOnUpdate(cache, mutationResult, activeStation.id),
          }
        : {
            variables: {
              stationId: formData.stationId,
              vitrineProduct,
            },
            update: (cache: any, mutationResult: any) => updateCacheOnCreate(cache, mutationResult, activeStation.id),
          },
    )
    this.props.onSubmit()
    this.onReset()
  }
  onReset = () => {
    this.setState({
      formData: { ...defaultFormData },
    })
  }

  render() {
    const { onCancel } = this.props
    const { formData } = this.state
    return (
      <Mutation mutation={formData.id ? updateVitrineProduct : createVitrineProduct}>
        {(onMutate) => (
          <form noValidate onSubmit={(e) => this.onSubmit(onMutate, e)} className={css.component}>
            <TextField
              autoFocus
              required
              label={i18n.t('admin.vitrineProducts.products.label.name')}
              value={formData.name}
              onChange={(e) => this.onInput('name', e.target.value)}
              className={css.textField}
            />
            <TextField
              className={css.textField}
              label={i18n.t('admin.vitrineProducts.products.label.wastePercentage')}
              type='number'
              value={formData.wastePercent || ''}
              onChange={(e) => this.onInput('wastePercent', e.target.value)}
              placeholder=''
            />
            <FormControl component='fieldset'>
              <FormLabel component='legend'>{i18n.t('admin.vitrineProducts.products.label.type')}</FormLabel>
              <RadioGroup
                aria-label={i18n.t('admin.vitrineProducts.products.label.type')}
                name='type'
                value={formData.type}
                onChange={(e) => this.onInput('type', e.target.value)}
              >
                <FormControlLabel
                  value='Salty'
                  control={<Radio />}
                  label={i18n.t('admin.vitrineProducts.products.label.salty')}
                />
                <FormControlLabel
                  value='Sweet'
                  control={<Radio />}
                  label={i18n.t('admin.vitrineProducts.products.label.sweet')}
                />
              </RadioGroup>
            </FormControl>
            <DialogActions className={css.actions}>
              <Button type='button' onClick={onCancel}>
                {i18n.t('buttons.cancel')}
              </Button>
              <Button type='submit' color='primary'>
                {i18n.t('buttons.save')}
              </Button>
            </DialogActions>
          </form>
        )}
      </Mutation>
    )
  }
}

export default VitrineProductForm
